import { TCardFacet, TFacet } from '@/types/collection'
import { isSpecialRewrittenCollectionUrl } from '../constants'
import { findMatchingFacetValue } from './facet-matching'

/**
 * ConcatSlugHelper - Centralized logic for handling concat slug operations
 * Consolidates all concat value extraction, normalization, and validation
 */
export class ConcatSlugHelper {
  /**
   * Extract concat value from URL pathname
   * @param pathname - Current URL pathname
   * @param originalSlug - Original collection slug
   * @returns Extracted concat value or null
   */
  static extractFromUrl(pathname: string, originalSlug: string): string | null {
    // Check if this is a special rewritten collection URL or slug that should prevent concat
    // Special rewritten URLs and certain slugs should NOT extract concat from pathname
    // Check both pathname and originalSlug (whichever matches)
    if (
      isSpecialRewrittenCollectionUrl(pathname) ||
      isSpecialRewrittenCollectionUrl(originalSlug)
    ) {
      return null
    }

    const pathSlug = pathname.replace(/^\/(?:[a-z]{2}\/)?collection\//, '')
    if (!pathSlug || pathSlug === originalSlug) {
      return null
    }

    const slugParts = pathSlug.split('-')
    const originalParts = originalSlug.split('-')

    // Find the difference (new filter values)
    const newParts = slugParts.slice(originalParts.length)
    if (newParts.length > 0) {
      // Return full remaining parts joined with hyphen
      return newParts.join('-')
    }

    return null
  }

  /**
   * Normalize concat value using facet matching
   * Maps raw concat value to proper facet value using longest-match strategy
   * @param concatValue - Raw concat value from URL
   * @param facets - Available facets
   * @param cardFacets - Available card facets
   * @returns Normalized concat value or null
   */
  static normalize(
    concatValue: string,
    facets: TFacet[],
    cardFacets: TCardFacet[]
  ): string | null {
    if (!concatValue) return null

    const match = findMatchingFacetValue(concatValue, facets, cardFacets)
    return match ? match.value.seo_alias : null
  }

  /**
   * Check if a facet key is a concat type
   * @param key - Facet key to check
   * @param facets - Available facets
   * @param cardFacets - Available card facets
   * @returns True if the facet is a concat type
   */
  static isConcatFacet(
    key: string,
    facets: TFacet[],
    cardFacets: TCardFacet[]
  ): boolean {
    return [...facets, ...cardFacets].some(
      (f) => f.seo_alias === key && f.is_concat_slug
    )
  }

  /**
   * Get the first concat facet from the facets list
   * @param facets - Available facets
   * @param cardFacets - Available card facets
   * @returns First concat facet or null
   */
  static getConcatFacet(
    facets: TFacet[],
    cardFacets: TCardFacet[]
  ): TFacet | TCardFacet | null {
    return [...facets, ...cardFacets].find((f) => f.is_concat_slug) ?? null
  }

  /**
   * Compute concat value from filters and facets
   * Helper function to calculate concat value from filter values
   * @param filters - Filter values from store
   * @param facets - Available facets
   * @param cardFacets - Available card facets
   * @returns Concat value (string) or null
   */
  static computeConcatValue(
    filters: Record<string, string | string[] | number | undefined>,
    facets: TFacet[],
    cardFacets: TCardFacet[]
  ): string | null {
    const concatFacet = this.getConcatFacet(facets, cardFacets)
    if (!concatFacet) return null

    const value = filters[concatFacet.seo_alias]
    if (!value) return null

    return Array.isArray(value) ? value[0] : String(value)
  }

  /**
   * Extract and normalize concat value from URL in one operation
   * @param pathname - Current URL pathname
   * @param originalSlug - Original collection slug
   * @param facets - Available facets
   * @param cardFacets - Available card facets
   * @returns Object with raw and normalized concat values
   */
  static extractAndNormalize(
    pathname: string,
    originalSlug: string,
    facets: TFacet[],
    cardFacets: TCardFacet[]
  ): {
    raw: string | null
    normalized: string | null
  } {
    // Check if pathname or slug is a special rewritten collection URL that should prevent concat
    // Special rewritten URLs and certain slugs should NOT extract concat from pathname
    // Use the full pathname (not just extracted part) to check
    if (
      isSpecialRewrittenCollectionUrl(pathname) ||
      isSpecialRewrittenCollectionUrl(originalSlug)
    ) {
      // Return null for special rewritten collections - they don't use concat in slug
      return { raw: null, normalized: null }
    }

    const raw = this.extractFromUrl(pathname, originalSlug)
    const normalized = raw ? this.normalize(raw, facets, cardFacets) : null

    return { raw, normalized }
  }

  /**
   * Validate if a concat value is valid for the given facets
   * @param concatValue - Concat value to validate
   * @param facets - Available facets
   * @param cardFacets - Available card facets
   * @returns True if the concat value is valid
   */
  static isValidConcatValue(
    concatValue: string,
    facets: TFacet[],
    cardFacets: TCardFacet[]
  ): boolean {
    if (!concatValue) return false

    const allFacets = [...facets, ...cardFacets]
    return allFacets.some((facet) => {
      if (!facet.is_concat_slug) return false
      return facet.facet_values?.some(
        (value) => value.seo_alias === concatValue
      )
    })
  }

  /**
   * Get all possible concat values from facets
   * @param facets - Available facets
   * @param cardFacets - Available card facets
   * @returns Array of all possible concat values
   */
  static getAllConcatValues(
    facets: TFacet[],
    cardFacets: TCardFacet[]
  ): string[] {
    const allFacets = [...facets, ...cardFacets]
    const concatValues: string[] = []

    allFacets.forEach((facet) => {
      if (facet.is_concat_slug && facet.facet_values) {
        facet.facet_values.forEach((value) => {
          concatValues.push(value.seo_alias)
        })
      }
    })

    return concatValues
  }

  /**
   * Extract base slug từ slug có thể có concat value
   * Được dùng để match với hardcode collection slugs
   * @param fullSlug - Full slug từ URL (có thể có concat value)
   * @param hardcodeSlugs - Optional array of hardcode slugs (để tránh circular dependency)
   * @returns Base slug (không có concat value)
   * @example
   * extractBaseSlugForHardcode("amazon-best-seller-trang") → "amazon-best-seller"
   * extractBaseSlugForHardcode("gym-den-vang") → "gym"
   * extractBaseSlugForHardcode("ao-thun-nam") → "ao-thun-nam" (không phải hardcode)
   */
  static extractBaseSlugForHardcode(
    fullSlug: string,
    hardcodeSlugs?: readonly string[]
  ): string {
    // Nếu không pass hardcodeSlugs, function sẽ return nguyên slug
    // Caller phải pass vào để function hoạt động đúng
    if (!hardcodeSlugs || hardcodeSlugs.length === 0) {
      return fullSlug
    }

    // Try matching với từng hardcode slug (longest first để match chính xác)
    // Sort by length descending để match slug dài nhất trước
    const sortedHardcodeSlugs = [...hardcodeSlugs].sort(
      (a, b) => b.length - a.length
    )

    for (const hardcodeSlug of sortedHardcodeSlugs) {
      if (fullSlug === hardcodeSlug) {
        // Exact match
        return hardcodeSlug
      }
      if (fullSlug.startsWith(hardcodeSlug + '-')) {
        // Has concat value (e.g., "gym-den" starts with "gym-")
        return hardcodeSlug
      }
    }

    // Không phải hardcode collection, return nguyên
    return fullSlug
  }
}
